import {
  type GetWorkspaceBillingStateQuery,
  type GetWorkspaceSubscriptionQuery,
  Permission,
} from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { WorkspaceServerService } from '../services/workspace-server';
import {
  type CancellationDeclaration,
  SubscriptionStore,
} from '../stores/subscription';

export type WorkspaceBillingStateType =
  GetWorkspaceBillingStateQuery['workspace']['billingState'];

type WorkspaceSubscriptionQuerySubscriptionType = NonNullable<
  GetWorkspaceSubscriptionQuery['workspace']['subscription']
>;
type WorkspaceBillingStateSubscriptionType = NonNullable<
  WorkspaceBillingStateType['billingSubscription']
>;

export type SubscriptionType =
  | WorkspaceSubscriptionQuerySubscriptionType
  | WorkspaceBillingStateSubscriptionType;

export function canReadWorkspaceBillingState(
  role: Permission | undefined
): boolean {
  return role === Permission.Owner;
}

export class WorkspaceSubscription extends Entity {
  subscription$ = new LiveData<SubscriptionType | null | undefined>(null);
  billingState$ = new LiveData<WorkspaceBillingStateType | null | undefined>(
    null
  );
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  /** Whether this workspace has an active paid subscription (plan-agnostic) */
  hasPaidPlan$ = this.subscription$.map(
    subscription => !!subscription && subscription.plan !== 'Free'
  );

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }
  server = this.workspaceServerService.server;
  store = this.workspaceServerService.server?.scope.get(SubscriptionStore);

  private hydrateCachedState(workspaceId: string) {
    if (!this.store) {
      return;
    }
    const cachedBillingState =
      this.store.getCachedWorkspaceBillingState(workspaceId);
    if (cachedBillingState) {
      this.billingState$.next(cachedBillingState);
      this.subscription$.next(cachedBillingState.billingSubscription);
      return;
    }
    const cachedSubscription =
      this.store.getCachedWorkspaceSubscription(workspaceId);
    if (cachedSubscription) {
      this.subscription$.next(cachedSubscription);
    }
  }

  async resumeSubscription(plan?: string) {
    if (!this.store) {
      throw new Error('Subscription store not available');
    }
    await this.store.mutateResumeSubscription(
      plan,
      undefined,
      this.workspaceService.workspace.id
    );
    await this.waitForRevalidation();
  }

  async cancelSubscription(
    plan?: string,
    declaration?: CancellationDeclaration
  ) {
    if (!this.store) {
      throw new Error('Subscription store not available');
    }
    await this.store.mutateCancelSubscription(
      plan,
      undefined,
      this.workspaceService.workspace.id,
      declaration
    );
    await this.waitForRevalidation();
  }

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler — without this,
    // waitFor resolves immediately before the fetch starts.
    this.isRevalidating$.next(true);
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(async signal => {
        const currentWorkspaceId = this.workspaceService.workspace.id;
        if (!currentWorkspaceId || !this.server) {
          return undefined; // no subscription if no user
        }

        const serverConfig = await this.server.features$.waitForNonNull(signal);

        if (!serverConfig.payment) {
          // No payment feature, no subscription
          return {
            workspaceId: currentWorkspaceId,
            subscription: null,
          };
        }
        if (
          !canReadWorkspaceBillingState(
            this.workspaceService.workspace.meta.role
          )
        ) {
          // Billing fields are owner-only. In particular, do not hydrate a
          // previous owner's cached billing state after a role downgrade.
          this.billingState$.next(undefined);
          this.subscription$.next(undefined);
          return {
            workspaceId: currentWorkspaceId,
            subscription: undefined,
          };
        }
        this.hydrateCachedState(currentWorkspaceId);
        if (!this.store) {
          return {
            workspaceId: currentWorkspaceId,
            subscription: null,
          };
        }
        const { workspaceId, billingState } =
          await this.store.fetchWorkspaceBillingState(
            currentWorkspaceId,
            signal
          );
        return {
          workspaceId: workspaceId,
          billingState,
          subscription: billingState.billingSubscription,
        };
      }).pipe(
        smartRetry(),
        tap(data => {
          if (!data) {
            return;
          }
          if (data.billingState && data.workspaceId && this.store) {
            this.store.setCachedWorkspaceBillingState(
              data.workspaceId,
              data.billingState
            );
            this.billingState$.next(data.billingState);
          } else {
            this.billingState$.next(data?.billingState ?? undefined);
          }
          if (data.subscription && data.workspaceId && this.store) {
            this.store.setCachedWorkspaceSubscription(
              data.workspaceId,
              data.subscription
            );
          }
          this.subscription$.next(data.subscription);
        }),
        catchErrorInto(this.error$),
        onStart(() => this.isRevalidating$.next(true)),
        onComplete(() => this.isRevalidating$.next(false))
      );
    })
  );

  reset() {
    this.subscription$.next(null);
    this.billingState$.next(null);
    this.isRevalidating$.next(false);
    this.error$.next(null);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
